import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { subject } from '@casl/ability';
import { useAbility } from '@casl/react';
import { CircularProgress, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Box } from '@mui/system';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import DataLoadingError from '@components/common/DataLoadingError';
import useEventDemographics from '@components/common/hooks/api/useEventDemographics';
import useBoothTopicsQuery from '@components/common/hooks/api/useBoothTopicsQuery';
import usePearsQuery from '@components/common/hooks/api/usePearsQuery';
import useSessionDetailsQuery from '@components/common/hooks/api/useSessionDetailsQuery';
import { API_URLS, axiosRequest } from '@lib/axios';
import moment from '@lib/moment';
import { AbilityContext, Subjects } from '@lib/permissions';
import { prepareAnswers, scrollToAlert } from '@lib/utils';
import UserContext from '@lib/contexts/userContext';
import { PageContext } from '@lib/contexts/pageContext';
import { shouldCollectDemographics, shouldTrackParticipants } from '../../eventFunctions';
import CompleteSessionForm from './CompleteSessionForm';
import CompleteSessionDetail from './CompleteSessionDetail';
import useFormQuery from '@components/common/hooks/api/useFormQuery';

const CompleteSession = ({ setTitle }) => {
  const [submitting, setSubmitting] = useState(false);

  const { eventID, sessionID } = useParams();

  const { authenticated, userState: { userRecord } } = useContext(UserContext);
  const {
    setPageMessage,
    setPageErrorMessage,
    appendMessages,
    appendErrorMessages,
    setBreadcrumbValue
  } = useContext(PageContext);

  const ability = useAbility(AbilityContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: session,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useSessionDetailsQuery(sessionID);

  const { data: formQuestions, isError: questionsError, isLoading: questionsLoading } = useFormQuery('COMPLETED');

  const [ previous_sessions, previous_participant_actions ] = useMemo(() => {
    if (!session) {
      return [];
    }
    const previous_sessions = session.event.sessions
      .filter(s => s.status === 'COMPLETED' &&
        moment(s.start).isBefore(moment(session.start)) &&
        s.id !== session.id);
    return [
      previous_sessions,
      previous_sessions.filter(s => !!s.completed?.participant_actions).map(s => s.completed.participant_actions)
    ];
  }, [session]);

  const boothQuery = useBoothTopicsQuery(session);

  const {
    data: pearsData,
    isLoading: pearsLoading,
    error: pearsError,
    isError: pearsIsError,
  } = usePearsQuery();

  const readOnly = useMemo(() => {
    if (authenticated && session) {
      return ability.cannot('edit', subject(Subjects.SESSION, session));
    } else {
      return null;
    }
  }, [authenticated, ability, session]);

  const { saveDemographics } = useEventDemographics(session?.event);

  const saveCompletedMutation = useMutation({
    mutationFn: async (input) => axiosRequest({
      method: 'POST',
      url: `${API_URLS.SESSIONS}/${sessionID}/complete`,
      data: input
    }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sessions'] }),
        queryClient.invalidateQueries({ queryKey: ['events'] }),
      ]);
    },
    onError: async (err) => {
      // setPageErrorMessage('Error saving session status');
      console.log('[CompleteSessionForm][save] error:', err);
    },
  });

  const updateSessionMutation = useMutation({
    mutationFn: async (updates) => axiosRequest({
      method: 'PUT',
      url: `${API_URLS.SESSIONS}/${sessionID}`,
      data: updates,
    }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sessions'] }),
        queryClient.invalidateQueries({ queryKey: ['events'] }),
      ]);
    },
    onError: async (err) => {
      setPageErrorMessage('Error saving session data', err);
      console.log('[CompleteSessionForm][updateSession] error:', err);
    },
  });

  const unfinalizeSessionMutation = useMutation({
    mutationFn: async () => axiosRequest({
      method: 'PUT',
      url: `${API_URLS.SESSIONS}/${session.id}`,
      data: {
        is_finalized: false,
      }
    }),
    onSuccess: async () => {
      setPageErrorMessage('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sessions'] }),
      ]);
      navigate(`/events/${session.event.id}/${session.id}/participants`);
    },
    onError: async (err) => {
      setPageErrorMessage(['Error unfinalizing session', err]);
      console.log('[CompleteSession][unfinalize] error:', err);
    },
  });

  const handleUpdateParticipants = () => {
    return unfinalizeSessionMutation.mutate();
  };


  /**
   * Form submit handler. Saves data to the DB. If this code runs, the validation has already passed.
   * Validation logic for this form is in CompleteSessionForm.js.
   */
  const onSubmit = async (values) => {
    setPageMessage(null);
    setPageErrorMessage(null);
    setSubmitting(true);

    const answers = prepareAnswers(formQuestions, values);

    const input = Object.keys(values).reduce((acc, key) => {
      const value = values[key];
      switch (key) {
        case 'lesson':
          if (value?.id) {
            acc[`${key}_id`] = value?.id;
          }
          break;
        case 'booth_topic_name':
        case 'lesson_name':
          acc[key] = value;
          break;
        case 'start':
        case 'end':
          acc[key] = moment(value).toISOString(true);
          break;
        case 'donations_total': {
          const floatVal = parseFloat(String(value).replaceAll(/[^0-9.]+/g, ''));
          acc[key] = isNaN(floatVal) ? null : floatVal;
          break;
        }
        case 'delivery_location':
          acc[key] = value?.label ?? value ?? null;
          acc[`${key}_id`] = value?.id ?? null;
          break;
        case 'participant_story':
        case 'as_planned_detail':
        case 'participant_actions':
        case 'delivery_method':
        case 'comments':
        case 'recipes':
          acc[key] = value?.id || value;
          break;
        case 'participant_count':
          // Int fields. Sending a float or other non-Int value causes API errors.
          // This mainly protects against user mischief like pasting a float value into a number field.
          acc[key] = parseInt(value);
          break;
        case 'participant_behaviors':
          acc[key] = value ? { efnep: value } : {};
          break;
        case 'delivery_language':
        case 'material_language':
          acc[key] = (value || []);
          break;
        case 'participant_has_story':
        case 'participant_permission':
        case 'as_planned':
        case 'demonstration':
        case 'donations_received':
          if (value !== null && value !== undefined) {
            acc[key] = value === 'true';
          }
          break;
        case 'collaborators':
          acc[key] = value ? value.map(v => v.id) : [];
          break;
        default: break;
      }
      return acc;
    }, {
      answers,
    });

    if (ability.cannot('edit', subject(Subjects.COMPLETED_SESSION, { ...session, ...input }))) {
      setSubmitting(false);
      setPageErrorMessage('You do not have permission to edit this session.');
      scrollToAlert();
      return false;
    }

    try {
      const saveAndComplete = values.save === 'complete';

      // Step 1: Save the form data

      const completedResult = await saveCompletedMutation.mutateAsync(input);
      const completedID = completedResult?.id;
      if (completedID) {
        appendMessages('Your answers on the Complete Session page are saved.');
      } else {
        appendErrorMessages('Failed to save Complete Session data');
        setSubmitting(false);
        return;
      }

      // Step 2: Update the session status and metadata

      if (saveAndComplete || values.save === 'update') {
        // Complete session if it was selected, and/or update the session modified timestamp
        const updateInput = { };
        if (saveAndComplete) {
          updateInput.status = 'COMPLETED';
          updateInput.completed_by = userRecord.email;
          updateInput.completedAt = moment().toISOString(true);
        }
        // Note: If updating values on a session that's already completed (values.save === 'update'), we
        // save with an empty payload, just to update the metadata.
        await updateSessionMutation.mutateAsync(updateInput);
      }

      // Step 3: Save demographics data. This belongs to the event, not the session.

      // We want to save demographics data if the following are true:
      // * It's a type of event we collect demographics for
      // * It's not an event we track individual participants for
      //   * If shouldTrackParticipants is true, the demographics are automatically calculated on the
      //     finalize participants page instead of here.
      // * Demographic data was previously entered, or the user answered "Yes" to "are you ready to enter demographics?"
      if (
        shouldCollectDemographics(session.event) &&
        !shouldTrackParticipants(session.event) &&
        (values.enter_demographics === 'yes' || !!session.event.demographics)
      ) {
        try {
          await saveDemographics(session.event, values);
        } catch (e) {
          console.error('Failed to save demographics data', e.errors);
          appendErrorMessages(['Failed to save demographics data.', ...(e?.errors ?? [])]);
          setSubmitting(false);
          return;
        }
      }

      // Query caching was stubborn on this page. Need to refetch, even though we're redirecting.
      // Otherwise the old data would appear in the demographics form the next time the user viewed it.
      await refetch();

      setSubmitting(false);
      navigate(`/events/${eventID}/${sessionID}`, {
        state: {
          message: saveAndComplete ? 'Session Completed' : 'Your answers on the Complete Session page are saved.',
        }
      });
    } catch (error) {
      console.log('Error completing session: ', error);
      setSubmitting(false);
      appendErrorMessages(['Error completing session.', ...(error?.errors ?? [])]);
      scrollToAlert();
    }
  };

  useEffect(() => {
    if (session) {
      setBreadcrumbValue(':event', session.event);
      setBreadcrumbValue(':session', session);
    }
    // eslint-disable-next-line
  }, [session]);

  const formReady = useMemo(() => {
    return !loading && !isError && session &&
      !pearsLoading && !pearsIsError && pearsData &&
      !boothQuery.isLoading && !boothQuery.isError && boothQuery.data &&
      !questionsLoading && !questionsError;
  }, [loading, isError, pearsLoading, pearsIsError, session, pearsData, boothQuery.isLoading, boothQuery.isError, boothQuery.data, questionsError, questionsLoading]);

  if (loading || pearsLoading) {
    return (
      <Box width="100%" textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (isError && error.status !== 404) {
    return <DataLoadingError title={'Failed to load session!'} errors={error.errors} />;
  }

  if (pearsIsError) {
    return <DataLoadingError title={'Failed to load PEARS data!'} errors={pearsError.errors} />;
  }

  if (!session) {
    return (
      <Typography variant="body1">
        You do not have access, or the session was not found.
      </Typography>
    );
  }

  if (session.status === 'CANCELED') {
    return (
      <Typography variant="body1">
        This session has been canceled. The Complete Session page is not available for canceled sessions.
      </Typography>
    );
  }

  if (readOnly && !session.completed) {
    return (
      <Typography variant="body1">
        The Complete Session page is not available for this session.
      </Typography>
    );
  }

  return (
    <Grid container gap={3} wrap={'nowrap'} columns={10}>
      <Grid xs={10}>
        { readOnly && session && session.completed && (
          <CompleteSessionDetail
            session={session}
            previous_sessions={previous_sessions}
            previous_participant_actions={previous_participant_actions}
          />
        )}
        { !readOnly && !!formReady && (
          <CompleteSessionForm
            session={session}
            previous_sessions={previous_sessions}
            previous_participant_actions={previous_participant_actions}
            pearsData={pearsData}
            boothTopics={boothQuery.data}
            questions={formQuestions}
            submitting={submitting}
            onSubmit={onSubmit}
            readOnly={readOnly}
            handleUpdateParticipants={handleUpdateParticipants}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default CompleteSession;
